import axios, { AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import cachedApi from '../utils/getCachedRequest';

export type TmsApiResponse<T> = {
  response: T | null;
  error?: string;
  loading: boolean;
};

const useTmsApi = <T>(
  endpoint: string,
  data: object | null = null,
  method: AxiosRequestConfig['method'] = 'post',
  cacheKey = '',
  shouldRunGet = true,
): TmsApiResponse<T> => {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { locale } = useIntl();

  useEffect(() => {
    if (method !== 'get' && !data) return;
    if (method === 'get' && !shouldRunGet) return;
    setLoading(true);
    const headers = {
      'User-Language': `${locale}-CA`,
    };
    // Call first API to get auth token
    const loginRequest = cachedApi.cachedRequest({
      key: 'auth-login',
      config: {
        method: 'POST',
        url: `${process.env.GATSBY_API_URL}/auth/login`,
        headers: {
          ...headers,
        },
      },
    });

    loginRequest
      // Set successful response token to session storage
      .then((tokenResponse) => {
        window.sessionStorage.setItem(
          'authToken',
          tokenResponse.data.access_token,
        );
        // Then call second API with auth token
        const tmsApiRequest = cacheKey
          ? cachedApi.cachedRequest({
            key: cacheKey,
            config: {
              url: `${process.env.GATSBY_API_URL}${endpoint}`,
              headers: {
                Authorization: `Bearer ${window.sessionStorage.authToken}`,
                ...headers,
              },
              data,
              method,
            },
          })
          : axios({
            url: `${process.env.GATSBY_API_URL}${endpoint}`,
            headers: {
              Authorization: `Bearer ${window.sessionStorage.authToken}`,
              ...headers,
            },
            data,
            method,
          });
        return tmsApiRequest
          .then((res) => {
            setResponse(res.data);
            setError('');
            return null;
          })
          .catch((err) => {
            setError(err);
            throw err;
          })
          .finally(() => {
            setLoading(false);
            return null;
          });
      })
      // Handle error for auth token
      .catch((tokenError) => {
        console.error(tokenError);
        setError(tokenError);
        setLoading(false);
        throw tokenError;
      });
  }, [cacheKey, data, endpoint, locale, method, shouldRunGet]);

  return { response, error, loading };
};

export default useTmsApi;
